import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiInfo, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import {
  getFabricDrift,
  type DriftAnalysisResponse,
  type DriftWorkspace,
  type DriftStatus,
} from '../../api/fabric';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<DriftStatus, { label: string; color: string; bg: string; dot: string }> = {
  Matched:   { label: 'Aligné',      color: '#059669', bg: '#ECFDF5', dot: '#059669' },
  Missing:   { label: 'Manquant',    color: '#DC2626', bg: '#FEF2F2', dot: '#DC2626' },
  Untracked: { label: 'Non tracé',   color: '#D97706', bg: '#FEF3C7', dot: '#D97706' },
};

const ITEM_ICONS: Record<string, string> = {
  fabric_lakehouse:            '/icons/fabric/lakehouse_64_item.svg',
  fabric_pipeline:             '/icons/fabric/pipeline_64_item.svg',
  fabric_notebook:             '/icons/fabric/notebook_64_item.svg',
  fabric_semantic_model:       '/icons/fabric/semantic_model_64_item.svg',
  fabric_report:               '/icons/fabric/power_bi_48_color.svg',
  fabric_warehouse:            '/icons/fabric/data_warehouse_64_item.svg',
  fabric_dataflow:             '/icons/fabric/dataflow_gen2_64_item.svg',
  fabric_eventstream:          '/icons/fabric/eventstream_64_item.svg',
  fabric_eventhouse:           '/icons/fabric/event_house_64_item.svg',
  fabric_ml_model:             '/icons/fabric/data_science_48_color.svg',
  fabric_deployment_pipeline:  '/icons/fabric/copy_job_64_item.svg',
  fabric_kql_database:         '/icons/fabric/kql_database_64_item.svg',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COLOR_GRADIENT: Record<string, string> = {
  '#6366F1': 'linear-gradient(135deg, #4338CA 0%, #818CF8 100%)',
  '#059669': 'linear-gradient(135deg, #065F46 0%, #10B981 100%)',
  '#2563EB': 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
  '#7C3AED': 'linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: DriftStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}30`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function MetricCard({ label, value, sub, color }: { label: string; value: number | string; sub?: string; color: string }) {
  const gradient = COLOR_GRADIENT[color] || `linear-gradient(135deg, ${color} 0%, ${color} 100%)`;
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: '1.25rem',
      background: gradient,
      borderRadius: '0.875rem',
      color: 'white',
      boxShadow: `0 4px 16px ${color}45`,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
      <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 4, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function HealthBadge({ score, status }: { score: number; status: string }) {
  const gradient = status === 'Healthy' ? 'linear-gradient(135deg, #065F46 0%, #10B981 100%)'
                 : status === 'Warning'  ? 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)'
                 :                         'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)';
  const shadow   = status === 'Healthy' ? 'rgba(16,185,129,0.35)' : status === 'Warning' ? 'rgba(245,158,11,0.35)' : 'rgba(239,68,68,0.35)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '1rem 1.25rem',
      background: gradient, color: 'white',
      boxShadow: `0 4px 16px ${shadow}`,
      borderRadius: '0.875rem',
      position: 'relative', overflow: 'hidden',
      minWidth: 160,
    }}>
      <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
      <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{score}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Health Score</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>{status}</div>
      </div>
    </div>
  );
}

function WorkspaceRow({ ws }: { ws: DriftWorkspace }) {
  const [open, setOpen] = useState(ws.status === 'Missing');
  const hasItems = ws.items.length > 0;
  const missingCount = ws.items.filter(i => i.status === 'Missing').length;

  return (
    <div style={{ borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
      <div
        onClick={() => hasItems && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: '#F9FAFB',
          cursor: hasItems ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        {hasItems
          ? (open ? <FiChevronDown size={14} color="#6B7280" /> : <FiChevronRight size={14} color="#6B7280" />)
          : <span style={{ width: 14 }} />
        }
        <img
          src="/icons/fabric/group_workspace_64_non-item.svg"
          alt="" style={{ width: 16, height: 16 }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#111827' }}>
          {ws.canvasName || ws.liveWsName}
        </span>
        {ws.liveWsName && ws.canvasName && ws.liveWsName !== ws.canvasName && (
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>→ {ws.liveWsName}</span>
        )}
        {missingCount > 0 && (
          <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>
            {missingCount} manquant{missingCount > 1 ? 's' : ''}
          </span>
        )}
        <StatusBadge status={ws.status} />
      </div>

      {open && hasItems && (
        <div style={{ padding: '8px 14px 12px 38px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {ws.items.map((item, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 10px', borderRadius: 6,
              background: item.status === 'Missing' ? '#FEF2F2'
                        : item.status === 'Untracked' ? '#FFF8E7'
                        : '#F0FDF4',
            }}>
              <img
                src={ITEM_ICONS[item.type] || '/icons/fabric/lakehouse_64_item.svg'}
                alt="" style={{ width: 14, height: 14 }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span style={{ flex: 1, fontSize: 12, color: '#374151' }}>{item.canvasName}</span>
              <span style={{ fontSize: 10, color: '#9CA3AF' }}>
                {item.type.replace('fabric_', '')}
              </span>
              <StatusBadge status={item.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const DriftTab: React.FC = () => {
  const [data, setData] = useState<DriftAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedArchs, setExpandedArchs] = useState<Set<string>>(new Set());
  const [showUntracked, setShowUntracked] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFabricDrift();
      setData(result);
      // Auto-expand architectures with missing workspaces
      const withIssues = new Set(
        result.architectures
          .filter(a => a.workspaces.some(w => w.status === 'Missing' || w.items.some(i => i.status === 'Missing')))
          .map(a => a.archId)
      );
      setExpandedArchs(withIssues);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleArch = (id: string) => setExpandedArchs(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const m = data?.metrics;

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
            Canvas ↔ Fabric Live
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>
            Comparaison des architectures GenIaC avec le runtime Microsoft Fabric
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, border: '1px solid #D1D5DB',
            background: 'white', cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 13, color: '#374151',
          }}
        >
          <FiRefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Analyse…' : 'Rafraîchir'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5',
          borderRadius: 10, color: '#DC2626', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Metrics */}
      {m && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <HealthBadge score={m.healthScore} status={m.healthStatus} />
          <MetricCard label="Architectures analysées" value={m.architecturesAnalyzed} color="#6366F1" />
          <MetricCard label="Workspaces alignés" value={`${m.workspacesMatched}/${m.workspacesInCanvas}`}
            sub={m.workspacesMissing > 0 ? `${m.workspacesMissing} manquant(s)` : 'Tout aligné'} color="#059669" />
          <MetricCard label="Artifacts alignés" value={`${m.itemsMatched}/${m.itemsMatched + m.itemsMissing}`}
            sub={m.itemsMissing > 0 ? `${m.itemsMissing} non déployé(s)` : 'Tout déployé'} color="#2563EB" />
          <MetricCard label="Live workspaces visibles" value={m.liveWorkspacesVisible}
            sub="via SP membre" color="#7C3AED" />
        </div>
      )}

      {/* Recommendations */}
      {data && data.recommendations.length > 0 && (
        <div style={{
          padding: '14px 18px', background: '#FFFBEB',
          border: '1px solid #FCD34D', borderRadius: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 10 }}>
            ⚠️ Recommandations ({data.recommendations.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {data.recommendations.map((rec, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#78350F' }}>
                {rec.type === 'warning'
                  ? <FiAlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0, color: '#D97706' }} />
                  : <FiInfo size={13} style={{ marginTop: 1, flexShrink: 0, color: '#2563EB' }} />
                }
                <span>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-architecture drift */}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {data.architectures.length === 0 && (
            <div style={{
              padding: '32px', textAlign: 'center', color: '#9CA3AF',
              background: '#F9FAFB', borderRadius: 12, border: '1px dashed #D1D5DB',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏗️</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Aucune architecture Fabric trouvée</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Créez une architecture avec le provider "Microsoft Fabric" pour démarrer l'analyse de drift.
              </div>
            </div>
          )}

          {data.architectures.map(arch => {
            const isOpen = expandedArchs.has(arch.archId);
            const issueCount = arch.workspaces.filter(w =>
              w.status === 'Missing' || w.items.some(i => i.status === 'Missing')
            ).length;

            return (
              <div key={arch.archId} style={{
                border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden',
              }}>
                {/* Arch header */}
                <div
                  onClick={() => toggleArch(arch.archId)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', background: '#F8FAFC',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  {isOpen
                    ? <FiChevronDown size={16} color="#6B7280" />
                    : <FiChevronRight size={16} color="#6B7280" />
                  }
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#111827' }}>
                    {arch.archName}
                  </span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {arch.workspaces.filter(w => w.status === 'Matched').length}/{arch.workspaces.length} WS alignés
                  </span>
                  {issueCount > 0 && (
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: '#FEF2F2', color: '#DC2626', fontWeight: 600,
                    }}>
                      {issueCount} drift{issueCount > 1 ? 's' : ''}
                    </span>
                  )}
                  {issueCount === 0 && arch.workspaces.length > 0 && (
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: '#ECFDF5', color: '#059669', fontWeight: 600,
                    }}>
                      ✓ Aligné
                    </span>
                  )}
                </div>

                {/* Arch workspaces */}
                {isOpen && (
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {arch.workspaces.length === 0 && (
                      <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0' }}>
                        Aucun workspace dans ce canvas.
                      </div>
                    )}
                    {arch.workspaces.map((ws, idx) => (
                      <WorkspaceRow key={ws.canvasId || `ws-${idx}`} ws={ws} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Untracked workspaces (visible live but not in any canvas) */}
          {data.untrackedWorkspaces.length > 0 && (
            <div style={{ border: '1px solid #FCD34D', borderRadius: 12, overflow: 'hidden' }}>
              <div
                onClick={() => setShowUntracked(s => !s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', background: '#FFFBEB', cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showUntracked
                  ? <FiChevronDown size={16} color="#D97706" />
                  : <FiChevronRight size={16} color="#D97706" />
                }
                <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#92400E' }}>
                  Workspaces non tracés dans GenIaC
                </span>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 10,
                  background: '#FEF3C7', color: '#D97706', fontWeight: 600,
                }}>
                  {data.untrackedWorkspaces.length} workspace{data.untrackedWorkspaces.length > 1 ? 's' : ''}
                </span>
              </div>
              {showUntracked && (
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.untrackedWorkspaces.map((ws, idx) => (
                    <WorkspaceRow key={ws.liveWsId || `uw-${idx}`} ws={ws} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DriftTab;
